import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  show(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', duration: number = 5000) {
    const toast: Toast = {
      id: this.generateId(),
      message,
      type,
      duration,
      timestamp: Date.now()
    };

    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, toast]);

    // Auto remove toast after duration
    if (duration > 0) {
      timer(duration).subscribe(() => {
        this.remove(toast.id);
      });
    }

    return toast.id;
  }

  success(message: string, duration: number = 4000) {
    return this.show(message, 'success', duration);
  }

  error(message: string, duration: number = 5000) {
    return this.show(message, 'error', duration);
  }

  warning(message: string, duration: number = 4000) {
    return this.show(message, 'warning', duration);
  }

  info(message: string, duration: number = 3000) {
    return this.show(message, 'info', duration);
  }

  remove(id: string) {
    const currentToasts = this.toastsSubject.value;
    const filteredToasts = currentToasts.filter(toast => toast.id !== id);
    this.toastsSubject.next(filteredToasts);
  }

  clear() {
    this.toastsSubject.next([]);
  }
}